import { useDisclosure } from '@mantine/hooks';
import { IconPlayerPlayFilled } from '@tabler/icons-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Text, Flex, Button, SelectProps } from '@mantine/core';

import Form from 'components/form';
import Badge from 'components/Badge';
import AuthContext from 'context/AuthContext';
import BillingContext from 'context/BillingContext';
import { capitalizeWords } from 'utils/string';
import BusinessContext from 'context/BusinessContext';
import { IVoiceResult } from 'interfaces/VoiceResult';
import { AccessLevel } from 'interfaces/GetUserResult';
import { UpgradePopover } from 'components/upgrade';
import { useFormComponentContext } from 'context/FormContext';

import classes from './VoiceProfile.module.scss';

const VoiceProfile = () => {
  const form = useFormComponentContext();
  const audioRef = useRef<HTMLAudioElement>(null);
  const voiceOptionsRef = useRef<Record<string, HTMLDivElement | null>>({});
  
  const {
    data: { account },
  } = useContext(AuthContext);
  const {
    actions: { setPricingOpened },
  } = useContext(BillingContext);
  const [
    openedVoiceOptions,
    { toggle: toggleVoiceOptions, close: closeVoiceOptions },
  ] = useDisclosure();
  
  // State for upgrade popovers - key is voice.value
  const [upgradePopovers, setUpgradePopovers] = useState<Record<string, boolean>>({});

  const previewDisabled = !form.values.voice || openedVoiceOptions;

  const {
    data: { voices },
    actions: { getVoices },
  } = useContext(BusinessContext);

  // Global click handler to close popovers when clicking outside
  useEffect(() => {
    const activePopovers = Object.entries(upgradePopovers).filter(([_, isOpen]) => isOpen);
    if (activePopovers.length === 0) return;
    
    function handleClickOutside(event: MouseEvent) {
      let shouldClose = true;
      
      // Check if click was inside any of our voice option elements
      // voiceValue is needed for Object.entries destructuring but not used in the loop logic
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [voiceValue, ref] of Object.entries(voiceOptionsRef.current)) {
        if (ref && ref.contains(event.target as Node)) {
          shouldClose = false;
          break;
        }
      }
      
      // Also check if click was inside a popover dropdown
      if (document.querySelector('.mantine-Popover-dropdown')?.contains(event.target as Node)) {
        shouldClose = false;
      }
      
      if (shouldClose) {
        setUpgradePopovers({});
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [upgradePopovers]);

  useEffect(() => {
    getVoices();
    // Intentionally excluding getVoices from dependencies as this effect should only
    // run once on component mount to load available voice options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVoiceOptions = (voices: IVoiceResult[]) => {
    return voices.map((voice) => {
      const label = capitalizeWords(voice.name);

      return {
        label,
        value: voice.name,
        disabled: account?.access_level !== AccessLevel.PRO && voice.is_premium,
      };
    });
  };

  const getAudioPath = (voice: string) => {
    return `/audio/voices/${capitalizeWords(voice)}.mp3`;
  };

  const playVoicePreview = (voice?: string) => {
    const selectedVoice = voice || form.values.voice;
    if (!selectedVoice) return;

    const audioPath = getAudioPath(selectedVoice);
    if (!audioPath) return;

    if (audioRef.current) {
      audioRef.current.src = audioPath;
      audioRef.current.play();
    }
  };

  const handleUpgrade = () => {
    // Close all popovers
    setUpgradePopovers({});
    
    // Open pricing modal using BillingContext
    setPricingOpened(true);
    
    // Track upgrade button click
    if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
      (window as any).analytics.track('premium_voice_upgrade_button_clicked');
    }
  };

  const setVoiceOptionRef = (voiceValue: string, element: HTMLDivElement | null) => {
    voiceOptionsRef.current[voiceValue] = element;
  };

  const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
    const premiumOnly = option.disabled;
    const isPremium = account?.access_level === AccessLevel.PRO;

    // Regular voice option (non-premium)
    if (!premiumOnly) {
      return (
        <Flex
          w="100%"
          p="var(--combobox-option-padding)"
          onClick={() => toggleVoiceOptions()}
        >
          {option.label}
        </Flex>
      );
    }

    // Premium voice option with upgrade popover
    return (
      <UpgradePopover
        opened={upgradePopovers[option.value] || false}
        onClose={() => {
          setUpgradePopovers(prev => ({
            ...prev,
            [option.value]: false
          }));
        }}
        upgradeTitle="Unlock Premium voices"
        upgradeDescription="Upgrade for higher-quality voices with improved speech patterns."
        onUpgrade={handleUpgrade}
        position="bottom"
        closeOnClickOutside={true}
        closeOnEscape
      >
        <Flex 
          ref={(el) => setVoiceOptionRef(option.value, el)}
          justify="space-between"
          gap="xs"
          w="100%"
          p="var(--combobox-option-padding)"
          className={`${classes.voiceOptionItem} ${
            upgradePopovers[option.value]
              ? classes.voiceOptionItemClicked
              : ''
          }`}
          onClick={() => {
            if (!isPremium) {
              setUpgradePopovers(prev => ({
                ...prev,
                [option.value]: true
              }));
              
              // Track premium voice popover shown
              if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
                (window as any).analytics.track('premium_voice_popover_shown', { voice: option.value });
              }
            }
          }}
        >
          {option.label}
          <Badge
            variant="light"
            style={{ fontSize: 12 }}
            color="var(--green-color)"
          >
            Premium
          </Badge>
        </Flex>
      </UpgradePopover>
    );
  };

  return (
    <Flex direction="column" gap={0} mb={10}>
      <audio ref={audioRef} />
      <Text size="sm" fw={500} mb={7}>
        Voice profile *
      </Text>
      <Flex
        justify="space-between"
        gap={{ base: 10, md: 'md' }}
        direction={{ base: 'column', xs: 'row' }}
      >
        <Form.Select
          m={0}
          flex={1}
          required
          showEdit
          name="voice"
          placeholder="Please select a profile"
          options={getVoiceOptions(voices)}
          renderOption={renderSelectOption}
          onClick={toggleVoiceOptions}
          dropdownOpened={openedVoiceOptions}
          onBlur={() => closeVoiceOptions()}
          classNames={{
            option: classes.voiceOption,
          }}
        />
        <Button
          mt={0}
          fz="sm"
          size="md"
          variant="default"
          h={{ xs: 'auto' }}
          w={{ base: '100%', xs: 'fit-content' }}
          bg="var(--bg-color)"
          onClick={() => playVoicePreview()}
          disabled={previewDisabled}
          rightSection={
            <IconPlayerPlayFilled
              size={17}
              color={
                previewDisabled ? 'var(--mantine-color-gray-5)' : '#00000073'
              }
            />
          }
        >
          Play voice preview
        </Button>
      </Flex>
    </Flex>
  );
};

export default VoiceProfile;
